import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { api, GOOGLE_WEB_CLIENT_ID, setKey, signedIn, signInWithGoogle, signOut, whoAmI } from "../api";

/** Gate + layout. Whatever credential is in the tab is checked against /admin/overview once. */
export function AdminApp() {
  const [ok, setOk] = useState<boolean | null>(signedIn() ? null : false);

  useEffect(() => {
    if (!signedIn()) return;
    api("/admin/overview").then(() => setOk(true)).catch(() => { signOut(); setOk(false); });
  }, []);

  if (ok === null) return <div className="admin"><main><p className="chrome">Checking…</p></main></div>;
  if (!ok) return <Login onDone={() => setOk(true)} />;

  const items: Array<[string, string]> = [
    ["/admin", "Overview"], ["/admin/spreads", "Chains"], ["/admin/feed", "Feed"], ["/admin/users", "Users"], ["/admin/feedback", "Feedback"], ["/admin/events", "Events"],
  ];
  return (
    <div className="admin">
      <nav>
        <NavLink to="/" className="brand" style={{ display: "flex", alignItems: "center", gap: 8 }}><img src="/favicon.svg" alt="" width={22} height={22} />BrainStorm</NavLink>
        {items.map(([to, label]) => (
          <NavLink key={to} to={to} end={to === "/admin"} className={({ isActive }) => `item${isActive ? " active" : ""}`}>{label}</NavLink>
        ))}
        <div className="foot">
          <div className="chrome" style={{ marginBottom: 8, overflowWrap: "anywhere" }}>{whoAmI()}</div>
          <button className="btn" onClick={() => { signOut(); location.href = "/admin"; }}>Sign out</button>
        </div>
      </nav>
      <main><Outlet /></main>
    </div>
  );
}

// Google Identity Services, loaded on demand. Types kept minimal on purpose.
interface Gsi {
  accounts: { id: {
    initialize(o: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: string; auto_select?: boolean }): void;
    renderButton(el: HTMLElement, o: Record<string, unknown>): void;
  } };
}
declare global { interface Window { google?: Gsi } }

function loadGsi(): Promise<Gsi> {
  return new Promise((resolve, reject) => {
    if (window.google) return resolve(window.google);
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client"; s.async = true;
    s.onload = () => (window.google ? resolve(window.google) : reject(new Error("GSI failed to load")));
    s.onerror = () => reject(new Error("GSI failed to load"));
    document.head.appendChild(s);
  });
}

function Login({ onDone }: { onDone: () => void }) {
  const [key, setK] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(!GOOGLE_WEB_CLIENT_ID);
  const btn = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!GOOGLE_WEB_CLIENT_ID || !btn.current) return;
    let cancelled = false;
    loadGsi().then((g) => {
      if (cancelled || !btn.current) return;
      g.accounts.id.initialize({
        client_id: GOOGLE_WEB_CLIENT_ID,
        callback: async ({ credential }) => {
          setBusy(true); setErr("");
          try {
            const r = await signInWithGoogle(credential);
            if (r.isAdmin) onDone();
            else setErr(`${r.email} is not an admin. Admins are listed in ADMIN_EMAILS on the API.`);
          } catch (ex) { setErr(ex instanceof Error ? ex.message : "Sign-in failed"); }
          finally { setBusy(false); }
        },
      });
      g.accounts.id.renderButton(btn.current, { theme: "outline", size: "large", text: "signin_with", shape: "pill", width: 280 });
    }).catch((e: Error) => setErr(e.message));
    return () => { cancelled = true; };
  }, []);

  const submitKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr("");
    setKey(key.trim());
    try { await api("/admin/overview"); onDone(); }
    catch (ex) { setKey(""); setErr(ex instanceof Error ? ex.message : "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="login">
      <div className="head">BrainStorm admin</div>
      <h2 style={{ margin: "8px 0 16px" }}>Sign in</h2>
      {GOOGLE_WEB_CLIENT_ID && (
        <>
          <p className="chrome">With the Google account that's on the admin list.</p>
          <div ref={btn} style={{ minHeight: 44, margin: "12px 0" }} />
          {busy && <p className="chrome">Checking…</p>}
        </>
      )}
      {err && <div className="msg err">{err}</div>}
      {!showKey && <button className="btn" style={{ marginTop: 16 }} onClick={() => setShowKey(true)}>Use the admin key instead</button>}
      {showKey && (
        <form onSubmit={submitKey} style={{ marginTop: GOOGLE_WEB_CLIENT_ID ? 24 : 0 }}>
          <p className="chrome">The value of <code>/brainstorm/prod/admin-key</code> in SSM Parameter Store. Kept in this tab only.</p>
          <input className="field" type="password" value={key} onChange={(e) => setK(e.target.value)} placeholder="admin key" autoFocus={!GOOGLE_WEB_CLIENT_ID} />
          <button className="btn primary" style={{ marginTop: 14 }} disabled={busy || !key}>Enter</button>
        </form>
      )}
    </div>
  );
}
